/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { cloneDeep } from 'lodash';

import { NeoFacetRequestApiModel } from '../../api/mentions/model/neo-facet-request-api.model';
import { NeoFacetDateFieldApiEnum } from '../../api/mentions/model/neo-facet-date-field-api.enum';

import { ReportsSeriesDataMapperService } from '../../Common/Reports/services/reports-series-data-mapper.service';
import { ReportsSeriesMapperService } from '../../Common/Reports/services/reports-series-mapper.service';
import { JournodexRegionsService } from '../../Common/Journodex/services/journodex-regions.service';
import { ReportsNeoFacetKeys } from '../../Common/Reports/enums/reports-neo-facet-keys.enum';
import { ReportSeriesFacetGeneratorEnum } from '../../Common/Reports/models/report-series-facet-generator.enum';
import { LocalizationService } from '../../Core/Localization/services/localization.service';
import { VISummarySeriesTypesEnum } from '../enums/vi-series-types.enum';
import { ViChartsService } from './vi-charts.service';
import { IndividualReportSeriesConfig } from './vi-reports-search.service';
import { ViRoutingService } from './vi-routing.service';

import { ChartMetricsEnum } from '../../Common/Reports/enums/chart-metrics.enum';
import { BucketFunctionApiEnum } from '../../api/mentions/enum/bucket-function-api.enum';
import { NeoFacetTermFieldApiEnum } from '../../api/mentions/enum/neo-facet-term-field-api.enum';
import { ViMonitor } from '../models/vi-monitor.model';

@Injectable()
export class ViSummaryChartService extends ViChartsService {

    hasData$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    readonly VISummarySeriesConfig: IndividualReportSeriesConfig[] = [
        // Brand Score
        {
            type: VISummarySeriesTypesEnum.brandScoreAvg,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                // hack to see if the monitor reports has any data
                this.hasData$.next(response?.totalResults > 0 ? true : false);

                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.viBrandScoreCount]?.[0]?.count || 0)
                    , 0);

                if (totalMentionCount) {
                    const avgValue = neoFacet?.buckets.reduce(
                        (previousValue, bucket) => {
                            if (bucket?.buckets?.[ReportsNeoFacetKeys.viBrandScoreCount]?.[0]?.count) {
                                return (previousValue || 0) +
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.viBrandScoreOverTime]?.[0]?.count || 0) *
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.viBrandScoreCount]?.[0]?.count/totalMentionCount);
                            }
                            return (previousValue || 0);
                        }, 0);
                    return [{
                        count: avgValue
                    }];
                } else {
                    return [{
                        count: null
                    }];
                }
            }
        },
        {
            type: VISummarySeriesTypesEnum.brandScoreAvgPP,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.viBrandScoreCount]?.[0]?.count || 0)
                    , 0);

                const avgValue = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => {
                        if (bucket?.buckets?.[ReportsNeoFacetKeys.viBrandScoreCount]?.[0]?.count) {
                            return (previousValue || 0) +
                                (bucket?.buckets?.[ReportsNeoFacetKeys.viBrandScoreOverTime]?.[0]?.count || 0) *
                                (bucket?.buckets?.[ReportsNeoFacetKeys.viBrandScoreCount]?.[0]?.count/totalMentionCount);
                        }
                        return (previousValue || 0);
                    }, 0);
                return [{
                    count: avgValue
                }];
            }
        },
        {
            type: VISummarySeriesTypesEnum.brandScoreTrend,
            metrics: [ChartMetricsEnum.volumeOfMentions],
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                return this.reportsSeriesDataMapperService.mapDateViDataFromNeoFacet(neoFacet, series, ReportsNeoFacetKeys.viBrandScoreOverTime);
            }
        },

        // Visibility Score
        {
            type: VISummarySeriesTypesEnum.visibilityScoreAvg,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.visibilityScoreCount]?.[0]?.count || 0)
                    , 0);

                if (totalMentionCount) {
                    const avgValue = neoFacet?.buckets.reduce(
                        (previousValue, bucket) => {
                            if (bucket?.buckets?.[ReportsNeoFacetKeys.visibilityScoreCount]?.[0]?.count) {
                                return (previousValue || 0) +
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.visibilityScoreOverTime]?.[0]?.count || 0) *
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.visibilityScoreCount]?.[0]?.count/totalMentionCount);
                            }
                            return (previousValue || 0);
                        }, 0);
                    return [{
                        count: avgValue
                    }];
                } else {
                    return [{
                        count: null
                    }];
                }

            }
        },
        {
            type: VISummarySeriesTypesEnum.visibilityScoreAvgPP,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.visibilityScoreCount]?.[0]?.count || 0)
                    , 0);

                const avgValue = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => {
                        if (bucket?.buckets?.[ReportsNeoFacetKeys.visibilityScoreCount]?.[0]?.count) {
                            return (previousValue || 0) +
                                (bucket?.buckets?.[ReportsNeoFacetKeys.visibilityScoreOverTime]?.[0]?.count || 0) *
                                (bucket?.buckets?.[ReportsNeoFacetKeys.visibilityScoreCount]?.[0]?.count/totalMentionCount);
                        }
                        return (previousValue || 0);
                    }, 0);
                return [{
                    count: avgValue
                }];
            }
        },
        {
            type: VISummarySeriesTypesEnum.visibilityScoreTrend,
            metrics: [ChartMetricsEnum.volumeOfMentions],
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                return this.reportsSeriesDataMapperService.mapDateViDataFromNeoFacet(neoFacet, series, ReportsNeoFacetKeys.visibilityScoreOverTime);
            }
        },

        // Presence Rate
        {
            type: VISummarySeriesTypesEnum.presenceRateAvg,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryTrendBooleanNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                let totalTrue = 0, totalFalse = 0;
                response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.viBooleanOverTimeFacet)?.buckets.forEach(
                    (bucket) => {
                        if (Array.isArray(bucket?.buckets?.[ReportsNeoFacetKeys.viBrandMentionTrend])) {
                            bucket?.buckets?.[ReportsNeoFacetKeys.viBrandMentionTrend].forEach((brand) => {
                                if (brand.termValue === 'TRUE') {
                                    totalTrue += brand.count;
                                } else if (brand.termValue === 'FALSE') {
                                    totalFalse += brand.count;
                                }
                            });
                        }
                    });
                return [{
                    count: (totalTrue + totalFalse) ? ((totalTrue / (totalTrue+totalFalse)) * 100) : null
                }];
            }
        },
        {
            type: VISummarySeriesTypesEnum.presenceRateAvgPP,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryTrendBooleanNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                let totalTrue = 0, totalFalse = 0;
                response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.viBooleanOverTimeFacet)?.buckets.forEach(
                    (bucket) => {
                        if (Array.isArray(bucket?.buckets?.[ReportsNeoFacetKeys.viBrandMentionTrend])) {
                            bucket?.buckets?.[ReportsNeoFacetKeys.viBrandMentionTrend].forEach((brand) => {
                                if (brand.termValue === 'TRUE') {
                                    totalTrue += brand.count;
                                } else if (brand.termValue === 'FALSE') {
                                    totalFalse += brand.count;
                                }
                            });
                        }
                    });
                return [{
                    count: ((totalTrue / (totalTrue+totalFalse)) * 100) || 0
                }];
            }
        },
        {
            type: VISummarySeriesTypesEnum.presenceRateTrend,
            metrics: [ChartMetricsEnum.volumeOfMentions],
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryTrendBooleanNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.viBooleanOverTimeFacet);
                return this.reportsSeriesDataMapperService.mapDateViDataFromNeoFacet(neoFacet, series, ReportsNeoFacetKeys.viBrandMentionTrend, true);
            }
        },

        // Average Rank When Listed
        {
            type: VISummarySeriesTypesEnum.rankWhenListedAvg,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.avgRankWhenListedCount]?.[0]?.count || 0)
                    , 0);

                if (totalMentionCount) {
                    const avgValue = neoFacet?.buckets.reduce(
                        (previousValue, bucket) => {
                            if (bucket?.buckets?.[ReportsNeoFacetKeys.avgRankWhenListedCount]?.[0]?.count) {
                                return (previousValue || 0) +
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.avgRankWhenListedOverTime]?.[0]?.count || 0) *
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.avgRankWhenListedCount]?.[0]?.count/totalMentionCount);
                            }
                            return (previousValue || 0);
                        }, 0);
                    return [{
                        count: avgValue
                    }];
                } else {
                    return [{
                        count: null
                    }];
                }
            }
        },
        {
            type: VISummarySeriesTypesEnum.rankWhenListedAvgPP,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.avgRankWhenListedCount]?.[0]?.count || 0)
                    , 0);

                const avgValue = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => {
                        if (bucket?.buckets?.[ReportsNeoFacetKeys.avgRankWhenListedCount]?.[0]?.count) {
                            return (previousValue || 0) +
                                (bucket?.buckets?.[ReportsNeoFacetKeys.avgRankWhenListedOverTime]?.[0]?.count || 0) *
                                (bucket?.buckets?.[ReportsNeoFacetKeys.avgRankWhenListedCount]?.[0]?.count/totalMentionCount);
                        }
                        return (previousValue || 0);
                    }, 0);
                return [{
                    count: avgValue
                }];
            }
        },
        {
            type: VISummarySeriesTypesEnum.rankWhenListedTrend,
            metrics: [ChartMetricsEnum.volumeOfMentions],
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                return this.reportsSeriesDataMapperService.mapDateViDataFromNeoFacet(neoFacet, series, ReportsNeoFacetKeys.avgRankWhenListedOverTime);
            }
        },

        // Sentiment Score
        {
            type: VISummarySeriesTypesEnum.sentimentScoreAvg,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.sentimentScoreCount]?.[0]?.count || 0)
                    , 0);

                if (totalMentionCount) {
                    const avgValue = neoFacet?.buckets.reduce(
                        (previousValue, bucket) => {
                            if (bucket?.buckets?.[ReportsNeoFacetKeys.sentimentScoreCount]?.[0]?.count) {
                                return (previousValue || 0) +
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.sentimentScoreOverTime]?.[0]?.count || 0) *
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.sentimentScoreCount]?.[0]?.count/totalMentionCount);
                            }
                            return (previousValue || 0);
                        }, 0);
                    return [{
                        count: avgValue
                    }];
                } else {
                    return [{
                        count: null
                    }];
                }
            }
        },
        {
            type: VISummarySeriesTypesEnum.sentimentScoreAvgPP,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.sentimentScoreCount]?.[0]?.count || 0)
                    , 0);

                const avgValue = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => {
                        if (bucket?.buckets?.[ReportsNeoFacetKeys.sentimentScoreCount]?.[0]?.count) {
                            return (previousValue || 0) +
                                (bucket?.buckets?.[ReportsNeoFacetKeys.sentimentScoreOverTime]?.[0]?.count || 0) *
                                (bucket?.buckets?.[ReportsNeoFacetKeys.sentimentScoreCount]?.[0]?.count/totalMentionCount);
                        }
                        return (previousValue || 0);
                    }, 0);
                return [{
                    count: avgValue
                }];
            }
        },
        {
            type: VISummarySeriesTypesEnum.sentimentScoreTrend,
            metrics: [ChartMetricsEnum.volumeOfMentions],
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                return this.reportsSeriesDataMapperService.mapDateViDataFromNeoFacet(neoFacet, series, ReportsNeoFacetKeys.sentimentScoreOverTime);
            }
        },

        // Accuracy Score
        {
            type: VISummarySeriesTypesEnum.accuracyScoreAvg,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.accuracyScoreCount]?.[0]?.count || 0)
                    , 0);

                if (totalMentionCount) {
                    const avgValue = neoFacet?.buckets.reduce(
                        (previousValue, bucket) => {
                            if (bucket?.buckets?.[ReportsNeoFacetKeys.accuracyScoreCount]?.[0]?.count) {
                                return (previousValue || 0) +
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.accuracyScoreOverTime]?.[0]?.count || 0) *
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.accuracyScoreCount]?.[0]?.count/totalMentionCount);
                            }
                            return (previousValue || 0);
                        }, 0);
                    return [{
                        count: avgValue
                    }];
                } else {
                    return [{
                        count: null
                    }];
                }
            }
        },
        {
            type: VISummarySeriesTypesEnum.accuracyScoreAvgPP,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.accuracyScoreCount]?.[0]?.count || 0)
                    , 0);

                const avgValue = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => {
                        if (bucket?.buckets?.[ReportsNeoFacetKeys.accuracyScoreCount]?.[0]?.count) {
                            return (previousValue || 0) +
                                (bucket?.buckets?.[ReportsNeoFacetKeys.accuracyScoreOverTime]?.[0]?.count || 0) *
                                (bucket?.buckets?.[ReportsNeoFacetKeys.accuracyScoreCount]?.[0]?.count/totalMentionCount);
                        }
                        return (previousValue || 0);
                    }, 0);
                return [{
                    count: avgValue
                }];
            }
        },
        {
            type: VISummarySeriesTypesEnum.accuracyScoreTrend,
            metrics: [ChartMetricsEnum.volumeOfMentions],
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                return this.reportsSeriesDataMapperService.mapDateViDataFromNeoFacet(neoFacet, series, ReportsNeoFacetKeys.accuracyScoreOverTime);
            }
        },

        // Risk Rate
        {
            type: VISummarySeriesTypesEnum.riskRateAvg,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryTrendBooleanNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                let totalTrue = 0, totalFalse = 0;
                response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.viBooleanOverTimeFacet)?.buckets.forEach(
                    (bucket) => {
                        if (Array.isArray(bucket?.buckets?.[ReportsNeoFacetKeys.viRiskFlag])) {
                            bucket?.buckets?.[ReportsNeoFacetKeys.viRiskFlag].forEach((risk) => {
                                if (risk.termValue === 'TRUE') {
                                    totalTrue += risk.count;
                                } else if (risk.termValue === 'FALSE') {
                                    totalFalse += risk.count;
                                }
                            });
                        }
                    });
                return [{
                    count: (totalTrue+totalFalse) ? ((totalTrue / (totalTrue+totalFalse)) * 100) : null
                }];
            }
        },
        {
            type: VISummarySeriesTypesEnum.riskRateAvgPP,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryTrendBooleanNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                let totalTrue = 0, totalFalse = 0;
                response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.viBooleanOverTimeFacet)?.buckets.forEach(
                    (bucket) => {
                        if (Array.isArray(bucket?.buckets?.[ReportsNeoFacetKeys.viRiskFlag])) {
                            bucket?.buckets?.[ReportsNeoFacetKeys.viRiskFlag].forEach((risk) => {
                                if (risk.termValue === 'TRUE') {
                                    totalTrue += risk.count;
                                } else if (risk.termValue === 'FALSE') {
                                    totalFalse += risk.count;
                                }
                            });
                        }
                    });
                return [{
                    count: ((totalTrue / (totalTrue+totalFalse)) * 100) || 0
                }];
            }
        },
        {
            type: VISummarySeriesTypesEnum.riskRateTrend,
            metrics: [ChartMetricsEnum.volumeOfMentions],
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryTrendBooleanNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.viBooleanOverTimeFacet);
                return this.reportsSeriesDataMapperService.mapDateViDataFromNeoFacet(neoFacet, series, ReportsNeoFacetKeys.viRiskFlag, true);
            }
        },

        // Average Citations per Scan
        {
            type: VISummarySeriesTypesEnum.citationPerScanAvg,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.avgCitationPerScanCount]?.[0]?.count || 0)
                    , 0);

                if (totalMentionCount) {
                    const avgValue = neoFacet?.buckets.reduce(
                        (previousValue, bucket) => {
                            if (bucket?.buckets?.[ReportsNeoFacetKeys.avgCitationPerScanCount]?.[0]?.count) {
                                return (previousValue || 0) +
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.avgCitationPerScanOverTime]?.[0]?.count || 0) *
                                    (bucket?.buckets?.[ReportsNeoFacetKeys.avgCitationPerScanCount]?.[0]?.count/totalMentionCount);
                            }
                            return (previousValue || 0);
                        }, 0);
                    return [{
                        count: avgValue
                    }];
                } else {
                    return [{
                        count: null
                    }];
                }
            }
        },
        {
            type: VISummarySeriesTypesEnum.citationPerScanAvgPP,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const totalMentionCount = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => (previousValue || 0) + (bucket?.buckets?.[ReportsNeoFacetKeys.avgCitationPerScanCount]?.[0]?.count || 0)
                    , 0);


                const avgValue = neoFacet?.buckets.reduce(
                    (previousValue, bucket) => {
                        if (bucket?.buckets?.[ReportsNeoFacetKeys.avgCitationPerScanCount]?.[0]?.count) {
                            return (previousValue || 0) +
                                (bucket?.buckets?.[ReportsNeoFacetKeys.avgCitationPerScanOverTime]?.[0]?.count || 0) *
                                (bucket?.buckets?.[ReportsNeoFacetKeys.avgCitationPerScanCount]?.[0]?.count/totalMentionCount);
                        }
                        return (previousValue || 0);
                    }, 0);
                return [{
                    count: avgValue
                }];
            }
        },
        {
            type: VISummarySeriesTypesEnum.citationPerScanTrend,
            metrics: [ChartMetricsEnum.volumeOfMentions],
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(summaryNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                return this.reportsSeriesDataMapperService.mapDateViDataFromNeoFacet(neoFacet, series, ReportsNeoFacetKeys.avgCitationPerScanOverTime);
            }
        },
    ];

    constructor(
        public localizationService: LocalizationService,
        public reportsSeriesMapperService: ReportsSeriesMapperService,
        public reportsSeriesDataMapperService: ReportsSeriesDataMapperService,
        public routingService: ViRoutingService,
        public journodexSearchService: JournodexRegionsService
    ) {
        super(routingService, localizationService, journodexSearchService);
        this.setChartConfig(this.VISummarySeriesConfig);
    }

    getHasData(): Observable<boolean> {
        return this.hasData$;
    }

    // Dynamically create the reports configs from the monitor competitors
    createDynamicCompetitiveChart(monitor: ViMonitor): IndividualReportSeriesConfig[] {
        if (!monitor || (monitor.competitors?.length === 0)) {
            return [];
        }
        const competitorsSeriesConfig: IndividualReportSeriesConfig[] = [];
        const dynamicNeoFacetRequest = cloneDeep(dynamicSummaryNeoFacetRequest);
        const dynamicBoolNeoFacetRequest = cloneDeep(dynamicBooleanNeoFacetRequest);

        // we have to fully build the dynamicNeoFacetRequest before creating the config, else we will create a neoFacetRequest for each competitor
        // and for that we have to loop throught the competitors twice...
        for (const competitor of monitor.competitors) {
            // VI_COMPETITOR_RANK
            const bucketRequestRankCount = {
                key: `VI_${competitor?.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_RANK_COUNT`,
                field: NeoFacetTermFieldApiEnum.VI_COMPETITOR_RANK,
                function: BucketFunctionApiEnum.INDIVIDUAL_COUNT,
                fieldKey: competitor.id
            };
            dynamicNeoFacetRequest.aggregateBucketRequests.push(bucketRequestRankCount);
            const bucketRequestRankAvg = {
                key: `VI_${competitor?.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_RANK_OVER_TIME`,
                field: NeoFacetTermFieldApiEnum.VI_COMPETITOR_RANK,
                function: BucketFunctionApiEnum.INDIVIDUAL_AVG,
                fieldKey: competitor.id
            };
            dynamicNeoFacetRequest.aggregateBucketRequests.push(bucketRequestRankAvg);

            // VI_COMPETITOR_SENTIMENT_SCORE
            const bucketRequestSentimentCount = {
                key: `VI_${competitor?.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_SENTIMENT_COUNT`,
                field: NeoFacetTermFieldApiEnum.VI_COMPETITOR_SENTIMENT_SCORE,
                function: BucketFunctionApiEnum.INDIVIDUAL_COUNT,
                fieldKey: competitor.id
            };
            dynamicNeoFacetRequest.aggregateBucketRequests.push(bucketRequestSentimentCount);
            const bucketRequestSentimentAvg = {
                key: `VI_${competitor?.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_SENTIMENT_OVER_TIME`,
                field: NeoFacetTermFieldApiEnum.VI_COMPETITOR_SENTIMENT_SCORE,
                function: BucketFunctionApiEnum.INDIVIDUAL_AVG,
                fieldKey: competitor.id
            };
            dynamicNeoFacetRequest.aggregateBucketRequests.push(bucketRequestSentimentAvg);

            // VI_COMPETITOR_MENTIONED
            const bucketRequestMentionedAvg = {
                key: `VI_${competitor?.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_MENTIONED_OVER_TIME`,
                field: NeoFacetTermFieldApiEnum.VI_COMPETITOR_MENTIONED,
                function: BucketFunctionApiEnum.INDIVIDUAL_AVG,
                fieldKey: competitor.id
            };
            dynamicBoolNeoFacetRequest.plainBucketRequests.push(bucketRequestMentionedAvg);

            // VI_COMPETITOR_DOMAIN_CITED
            const bucketRequestDomainCitedAvg = {
                key: `VI_${competitor?.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_DOMAIN_CITED_OVER_TIME`,
                field: NeoFacetTermFieldApiEnum.VI_COMPETITOR_DOMAIN_CITED,
                function: BucketFunctionApiEnum.INDIVIDUAL_AVG,
                fieldKey: competitor.id
            };
            dynamicBoolNeoFacetRequest.plainBucketRequests.push(bucketRequestDomainCitedAvg);
        };

        const ownCardKey = `VI_${monitor?.brand?.name?.toUpperCase().replace(/\s+/g, '_')}`;

        // card chart
        competitorsSeriesConfig.push({
            type: VISummarySeriesTypesEnum.competitiveScoreAvg,
            dynamicKey: ownCardKey,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(dynamicNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const dynamicNeoFacet = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const dynamicNeoFacetBoolean = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.viBooleanOverTimeFacet);
                if (!dynamicNeoFacet || !dynamicNeoFacetBoolean) {
                    return [];
                }

                let dataPoints = this.reportsSeriesDataMapperService.calculateStrengthScoreFromNeoFacet(
                    dynamicNeoFacet,
                    dynamicNeoFacetBoolean,
                    ReportsNeoFacetKeys.avgRankWhenListedCount,
                    ReportsNeoFacetKeys.avgRankWhenListedOverTime,
                    ReportsNeoFacetKeys.sentimentScoreCount,
                    ReportsNeoFacetKeys.sentimentScoreOverTime,
                    ReportsNeoFacetKeys.viBrandMentionTrend,
                    ReportsNeoFacetKeys.ownDomainCited,
                    monitor?.brand?.name
                );

                dataPoints = dataPoints.filter(x => !!x.value);
                const totalDataPoints = dataPoints.reduce((previousValue, dataPoint) => (previousValue || 0) + (Number(dataPoint.total) || 0), 0);
                const total = dataPoints.reduce((previousValue, dataPoint) => {
                    return (previousValue || 0) + (dataPoint.value * ((Number(dataPoint.total) / (totalDataPoints)) || 0));
                }, 0);

                return [{
                    label: monitor?.brand?.name,
                    mappingKeys: {
                        source:  monitor?.brand?.name,
                    },
                    value: total,
                    valueLabel: ownCardKey,
                }];
            }
        });

        // trend chart
        competitorsSeriesConfig.push({
            type: VISummarySeriesTypesEnum.competitiveScoreTrend,
            dynamicKey:  monitor?.brand?.name,
            facetGenerator: () => {
                return {
                    facet: JSON.parse(JSON.stringify(dynamicBoolNeoFacetRequest)),
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                const dynamicNeoFacet = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const dynamicNeoFacetBoolean = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.viBooleanOverTimeFacet);
                if (!dynamicNeoFacet || !dynamicNeoFacetBoolean) {
                    return [];
                }

                return this.reportsSeriesDataMapperService.calculateStrengthScoreFromNeoFacet(
                    dynamicNeoFacet,
                    dynamicNeoFacetBoolean,
                    ReportsNeoFacetKeys.avgRankWhenListedCount,
                    ReportsNeoFacetKeys.avgRankWhenListedOverTime,
                    ReportsNeoFacetKeys.sentimentScoreCount,
                    ReportsNeoFacetKeys.sentimentScoreOverTime,
                    ReportsNeoFacetKeys.viBrandMentionTrend,
                    ReportsNeoFacetKeys.ownDomainCited,
                    monitor?.brand?.name
                );
            }
        });

        for (const competitor of monitor.competitors) {
            const cardKey = `VI_${competitor?.name?.toUpperCase().replace(/\s+/g, '_')}`;

            // card chart
            competitorsSeriesConfig.push({
                type: VISummarySeriesTypesEnum.competitiveScoreAvg,
                dynamicKey: cardKey,
                facetGenerator: () => {
                    return {
                        facet: JSON.parse(JSON.stringify(dynamicNeoFacetRequest)),
                        identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                    };
                },
                dataMapper: (response, request, series) => {
                    const dynamicNeoFacet = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.dynamicOvertimeFacet);
                    const dynamicNeoFacetBoolean = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.viDynamicBooleanOverTimeFacet);
                    if (!dynamicNeoFacet || !dynamicNeoFacetBoolean) {
                        return [];
                    }

                    let dataPoints = this.reportsSeriesDataMapperService.calculateStrengthScoreFromNeoFacet(
                        dynamicNeoFacet,
                        dynamicNeoFacetBoolean,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_RANK_COUNT`,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_RANK_OVER_TIME`,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_SENTIMENT_COUNT`,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_SENTIMENT_OVER_TIME`,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_MENTIONED_OVER_TIME`,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_DOMAIN_CITED_OVER_TIME`,
                        competitor.name
                    );

                    dataPoints = dataPoints.filter(x => !!x.value);
                    const totalDataPoints = dataPoints.reduce((previousValue, dataPoint) => (previousValue || 0) + (Number(dataPoint.total) || 0), 0);
                    const total = dataPoints.reduce((previousValue, dataPoint) => {
                        return (previousValue || 0) + (dataPoint.value * ((Number(dataPoint.total) / (totalDataPoints)) || 0));
                    }, 0);
                    return [{
                        label: competitor.name,
                        mappingKeys: {
                            source: competitor.name,
                        },
                        value: total,
                        valueLabel: cardKey,
                    }];
                }
            });

            // trend chart
            competitorsSeriesConfig.push({
                type: VISummarySeriesTypesEnum.competitiveScoreTrend,
                dynamicKey: competitor.name,
                facetGenerator: () => {
                    return {
                        facet: JSON.parse(JSON.stringify(dynamicBoolNeoFacetRequest)),
                        identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                    };
                },
                dataMapper: (response, request, series) => {
                    const dynamicNeoFacet = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.dynamicOvertimeFacet);
                    const dynamicNeoFacetBoolean = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.viDynamicBooleanOverTimeFacet);
                    if (!dynamicNeoFacet || !dynamicNeoFacetBoolean) {
                        return [];
                    }

                    return this.reportsSeriesDataMapperService.calculateStrengthScoreFromNeoFacet(
                        dynamicNeoFacet,
                        dynamicNeoFacetBoolean,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_RANK_COUNT`,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_RANK_OVER_TIME`,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_SENTIMENT_COUNT`,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_SENTIMENT_OVER_TIME`,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_MENTIONED_OVER_TIME`,
                        `VI_${competitor.name?.toUpperCase().replace(/\s+/g, '_')}_COMPETITOR_DOMAIN_CITED_OVER_TIME`,
                        competitor.name
                    );
                }
            });
        }

        return competitorsSeriesConfig;
    }
}


const summaryTrendBooleanNeoFacetRequest: NeoFacetRequestApiModel = {
    dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
    key: ReportsNeoFacetKeys.viBooleanOverTimeFacet,
    plainBucketRequests: [
        {
            // presence rate
            key: ReportsNeoFacetKeys.viBrandMentionTrend,
            field: NeoFacetTermFieldApiEnum.VI_BRAND_MENTIONED
        },
        {
            key: ReportsNeoFacetKeys.viRiskFlag,
            field: NeoFacetTermFieldApiEnum.VI_RISK_FLAG
        },
        {
            key: ReportsNeoFacetKeys.ownDomainCited,
            field: NeoFacetTermFieldApiEnum.VI_OWN_DOMAIN_CITED
        }
    ]
};

const summaryNeoFacetRequest: NeoFacetRequestApiModel = {
    dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
    key: ReportsNeoFacetKeys.overtimeFacet,
    aggregateBucketRequests: [
        {
            key: ReportsNeoFacetKeys.viBrandScoreOverTime,
            field: NeoFacetTermFieldApiEnum.VI_BRAND_SCORE,
            function: BucketFunctionApiEnum.INDIVIDUAL_AVG
        },
        {
            key: ReportsNeoFacetKeys.viBrandScoreCount,
            field: NeoFacetTermFieldApiEnum.VI_BRAND_SCORE,
            function: BucketFunctionApiEnum.INDIVIDUAL_COUNT
        },
        {
            key: ReportsNeoFacetKeys.visibilityScoreOverTime,
            field: NeoFacetTermFieldApiEnum.VI_ORGANIC_INCLUSION,
            function: BucketFunctionApiEnum.INDIVIDUAL_AVG
        },
        {
            key: ReportsNeoFacetKeys.visibilityScoreCount,
            field: NeoFacetTermFieldApiEnum.VI_ORGANIC_INCLUSION,
            function: BucketFunctionApiEnum.INDIVIDUAL_COUNT
        },
        {
            key: ReportsNeoFacetKeys.avgRankWhenListedOverTime,
            field: NeoFacetTermFieldApiEnum.VI_MENTION_RANK,
            function: BucketFunctionApiEnum.INDIVIDUAL_AVG
        },
        {
            key: ReportsNeoFacetKeys.avgRankWhenListedCount,
            field: NeoFacetTermFieldApiEnum.VI_MENTION_RANK,
            function: BucketFunctionApiEnum.INDIVIDUAL_COUNT
        },
        {
            key: ReportsNeoFacetKeys.sentimentScoreOverTime,
            field: NeoFacetTermFieldApiEnum.VI_SENTIMENT_SCORE,
            function: BucketFunctionApiEnum.INDIVIDUAL_AVG
        },
        {
            key: ReportsNeoFacetKeys.sentimentScoreCount,
            field: NeoFacetTermFieldApiEnum.VI_SENTIMENT_SCORE,
            function: BucketFunctionApiEnum.INDIVIDUAL_COUNT
        },
        {
            key: ReportsNeoFacetKeys.accuracyScoreOverTime,
            field: NeoFacetTermFieldApiEnum.VI_FACTUAL_ACCURACY,
            function: BucketFunctionApiEnum.INDIVIDUAL_AVG
        },
        {
            key: ReportsNeoFacetKeys.accuracyScoreCount,
            field: NeoFacetTermFieldApiEnum.VI_FACTUAL_ACCURACY,
            function: BucketFunctionApiEnum.INDIVIDUAL_COUNT
        },
        {
            key: ReportsNeoFacetKeys.avgCitationPerScanOverTime,
            field: NeoFacetTermFieldApiEnum.VI_CITATION_COUNT,
            function: BucketFunctionApiEnum.INDIVIDUAL_AVG
        },
        {
            key: ReportsNeoFacetKeys.avgCitationPerScanCount,
            field: NeoFacetTermFieldApiEnum.VI_CITATION_COUNT,
            function: BucketFunctionApiEnum.INDIVIDUAL_COUNT
        }
    ]
};


const dynamicBooleanNeoFacetRequest: NeoFacetRequestApiModel = {
    dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
    key: ReportsNeoFacetKeys.viDynamicBooleanOverTimeFacet,
    plainBucketRequests: []
};

const dynamicSummaryNeoFacetRequest: NeoFacetRequestApiModel = {
    dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
    key: ReportsNeoFacetKeys.dynamicOvertimeFacet,
    aggregateBucketRequests: []
};
